// NBA share graphics — the Built-to-hit record card. A branded 1080×1350 PNG drawn on a
// canvas (system fonts, flat fills, no external assets so it always renders) and handed
// to the native share sheet, falling back to a download. Self-contained on purpose.
const C = {
  bg: '#0A0E14', card: '#111827', line: '#1E2A3A', green: '#00D97E', orange: '#FF8A3D',
  amber: '#F5B841', red: '#F0616D', text: '#F1F5F9', muted: '#A6B2C4', faint: '#5A6675',
}
const W = 1080, H = 1350
const LABEL = { points: 'Points', rebounds: 'Rebounds', assists: 'Assists', threes: 'Threes', pra: 'PRA' }
const pct = v => (v == null ? '—' : `${Math.round(v * 100)}%`)

function rr(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

function base(ctx) {
  ctx.fillStyle = C.bg
  ctx.fillRect(0, 0, W, H)
  const g = ctx.createRadialGradient(W / 2, -120, 60, W / 2, -120, 760)
  g.addColorStop(0, 'rgba(0,217,126,0.20)')
  g.addColorStop(1, 'rgba(0,217,126,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, W, 760)
  ctx.textAlign = 'left'
  ctx.font = '900 52px system-ui, -apple-system, Arial'
  ctx.fillStyle = C.text
  ctx.fillText('LINE', 72, 130)
  const lw = ctx.measureText('LINE').width
  ctx.fillStyle = C.green
  ctx.fillText('BRKR', 72 + lw, 130)
  const bx = 72 + lw + ctx.measureText('BRKR').width + 20
  ctx.fillStyle = C.orange
  rr(ctx, bx, 96, 96, 42, 8); ctx.fill()
  ctx.fillStyle = '#1a0d04'
  ctx.font = '800 26px system-ui, Arial'
  ctx.fillText('NBA', bx + 20, 126)
}

function footer(ctx) {
  ctx.textAlign = 'center'
  ctx.font = '800 26px system-ui, Arial'
  ctx.fillStyle = C.green
  ctx.fillText('✓ CALIBRATED · GRADED ON THE BOX SCORE · linebrkr.app', W / 2, H - 100)
  ctx.font = '500 24px system-ui, Arial'
  ctx.fillStyle = C.faint
  ctx.fillText('LINEBRKR · research tool, not betting advice · 21+', W / 2, H - 56)
}

const rateColor = v => (v == null ? C.faint : v >= 0.8 ? C.green : v >= 0.7 ? C.amber : C.red)

// rec: record_status payload ({ built_to_hit: {hits, misses, hit_rate, expected, by_prop, by_date},
// replay_hit_rate }), season: start year.
export function drawBthRecordCard(rec, season) {
  const b = rec?.built_to_hit || {}
  const cv = document.createElement('canvas')
  cv.width = W; cv.height = H
  const ctx = cv.getContext('2d')
  base(ctx)

  ctx.textAlign = 'left'
  ctx.font = '800 30px system-ui, Arial'
  ctx.fillStyle = C.green
  ctx.fillText(`🎯 BUILT TO HIT · ${season}-${String(season + 1).slice(2)} SEASON`, 72, 236)

  // headline
  ctx.textAlign = 'center'
  ctx.font = '900 280px system-ui, -apple-system, Arial'
  ctx.fillStyle = C.green
  ctx.fillText(pct(b.hit_rate), W / 2, 500)
  ctx.font = '900 64px ui-monospace, Menlo, monospace'
  ctx.fillStyle = C.text
  ctx.fillText(b.graded ? `${b.hits}-${b.misses}` : '—', W / 2, 584)
  ctx.font = '500 30px system-ui, Arial'
  ctx.fillStyle = C.muted
  const ctxBits = []
  if (b.expected != null) ctxBits.push(`Model said ${pct(b.expected)}`)
  if (rec?.replay_hit_rate != null) ctxBits.push(`2025 replay ${(rec.replay_hit_rate * 100).toFixed(1)}%`)
  ctx.fillText(ctxBits.join('   ·   '), W / 2, 636)

  // by-stat tiles (up to 4, biggest samples first)
  const props = Object.entries(b.by_prop || {}).filter(([, v]) => v.graded)
    .sort((a, z) => z[1].graded - a[1].graded).slice(0, 4)
  const n = Math.max(props.length, 1)
  const gx = 72, gw = W - 144, gap = 20, tw = (gw - gap * (n - 1)) / n, ty = 690, th = 170
  props.forEach(([k, v], i) => {
    const x = gx + i * (tw + gap)
    ctx.fillStyle = C.card
    rr(ctx, x, ty, tw, th, 20); ctx.fill()
    ctx.strokeStyle = C.line; ctx.lineWidth = 2
    rr(ctx, x, ty, tw, th, 20); ctx.stroke()
    ctx.textAlign = 'center'
    ctx.font = '900 64px system-ui, -apple-system, Arial'
    ctx.fillStyle = rateColor(v.hit_rate)
    ctx.fillText(pct(v.hit_rate), x + tw / 2, ty + 84)
    ctx.font = '700 25px system-ui, Arial'
    ctx.fillStyle = C.text
    ctx.fillText((LABEL[k] || k).toUpperCase(), x + tw / 2, ty + 122)
    ctx.font = '500 22px system-ui, Arial'
    ctx.fillStyle = C.faint
    ctx.fillText(v.hits != null ? `${v.hits}-${v.misses}` : `${v.graded} graded`, x + tw / 2, ty + 152)
  })

  // night-by-night bars
  const nights = Object.entries(b.by_date || {}).filter(([, v]) => v.graded).slice(-12)
  const cy = 900, ch = 250
  ctx.fillStyle = C.card
  rr(ctx, gx, cy, gw, ch, 20); ctx.fill()
  ctx.strokeStyle = C.line; ctx.lineWidth = 2
  rr(ctx, gx, cy, gw, ch, 20); ctx.stroke()
  ctx.textAlign = 'left'
  ctx.font = '800 22px system-ui, Arial'
  ctx.fillStyle = C.muted
  ctx.fillText('NIGHT BY NIGHT', gx + 28, cy + 42)
  if (nights.length) {
    const px = gx + 28, pw = gw - 56, py = cy + 64, ph = 130
    const lo = 0.5, hi = 1.0, yOf = v => py + ph - ((Math.max(lo, Math.min(hi, v)) - lo) / (hi - lo)) * ph
    // 80% target line
    ctx.strokeStyle = 'rgba(0,217,126,0.45)'; ctx.setLineDash([8, 8]); ctx.lineWidth = 2
    ctx.beginPath(); ctx.moveTo(px, yOf(0.8)); ctx.lineTo(px + pw, yOf(0.8)); ctx.stroke(); ctx.setLineDash([])
    ctx.textAlign = 'right'; ctx.font = '700 20px system-ui, Arial'; ctx.fillStyle = C.green
    ctx.fillText('80% target', gx + gw - 28, cy + 42)
    const slot = pw / nights.length, bw = Math.min(56, slot * 0.62)
    nights.forEach(([d, v], i) => {
      const x = px + i * slot + (slot - bw) / 2, y = yOf(v.hit_rate)
      ctx.fillStyle = rateColor(v.hit_rate)
      rr(ctx, x, y, bw, py + ph - y, 8); ctx.fill()
      ctx.textAlign = 'center'
      ctx.font = '600 19px system-ui, Arial'; ctx.fillStyle = C.faint
      const [, mo, da] = d.split('-')
      ctx.fillText(`${+mo}/${+da}`, x + bw / 2, py + ph + 28)
    })
  }

  footer(ctx)
  return cv
}

export function shareCanvas(cv, filename, text) {
  return new Promise(resolve => {
    cv.toBlob(async blob => {
      if (!blob) { resolve(false); return }
      const file = new File([blob], filename, { type: 'image/png' })
      try {
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text })
          resolve(true); return
        }
      } catch (_) { /* cancelled / unsupported → download */ }
      try {
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = filename; a.click()
        setTimeout(() => URL.revokeObjectURL(url), 4000)
      } catch (_) {}
      resolve(true)
    }, 'image/png')
  })
}

export function shareBthRecord(rec, season) {
  const b = rec?.built_to_hit || {}
  const text = b.graded
    ? `LINEBRKR NBA Built to hit: ${b.hits}-${b.misses} (${pct(b.hit_rate)}) this season, graded on the box score.`
    : 'LINEBRKR NBA Built to hit'
  return shareCanvas(drawBthRecordCard(rec, season), 'linebrkr-nba-built-to-hit.png', text)
}
